import {
  useReducer,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { initialState } from "./Groups.model";
import { GROUPS_ACTIONS } from "./Groups.actions";
import { GroupsReducer } from "./Groups.reducers";
import { GroupsServices } from "./Groups.services";
import GROUPS_CONSTANTS, { GROUPS_FACILITIES_PICKER } from "./GroupsConstant";

export const useGroupsViewInitialization = () => {
  const [state, dispatch] = useReducer(GroupsReducer, initialState);
  const services = GroupsServices();
  const actions = GroupssActions(dispatch, services);
  return { state, actions };
};

const GroupssActions = (dispatch, services) => {
  const actions = {
    getAllGroups: async () => {
      dispatch({
        type: GROUPS_ACTIONS.LOADING,
        payload: true,
      });
      const response = await services.getAllGroups();
      if (response?.error) {
        dispatch({
          type: GROUPS_ACTIONS.ERROR,
          payload: response?.error ?? GROUPS_CONSTANTS.toast.MESSAGE_FAILURE,
        });
      } else {
        const sortedList =
          response?.data?.data?.sort((a, b) => a.name.localeCompare(b.name)) ?? [];

        dispatch({
          type: GROUPS_ACTIONS.GROUPSLIST,
          payload: sortedList,
        });
      }
      dispatch({
        type: GROUPS_ACTIONS.LOADING,
        payload: false,
      });
    },
    changeViewStatus: (status, selected_group) => {
      dispatch({
        type: GROUPS_ACTIONS.SELECTEDVIEW,
        payload: { status: status, group: selected_group },
      });
    },
    addNewGroup: async (groupInfo, onSuccessCallBack) => {
      dispatch({
        type: GROUPS_ACTIONS.LOADING,
        payload: true,
      });
      const response = await services.addNewGroup(groupInfo);
      if (response?.error) {
        dispatch({
          type: GROUPS_ACTIONS.ERROR,
          payload: response?.error ?? GROUPS_CONSTANTS.toast.MESSAGE_FAILURE,
        });
      } else {
        onSuccessCallBack(response.data);
      }
      dispatch({
        type: GROUPS_ACTIONS.LOADING,
        payload: false,
      });
    },
    editGroupInfo: async (groupInfo, status, onSuccessCallBack) => {
      dispatch({
        type: GROUPS_ACTIONS.LOADING,
        payload: true,
      });
      const response = await services.editGroup(groupInfo);
      if (response?.error) {
        dispatch({
          type: GROUPS_ACTIONS.ERROR,
          payload: response?.error ?? GROUPS_CONSTANTS.toast.MESSAGE_FAILURE,
        });
      } else {
        if (response.data) {
          onSuccessCallBack(response.data);
        }
        dispatch({
          type: GROUPS_ACTIONS.SELECTEDVIEW,
          payload: { status: status, group: null },
        });
      }
      dispatch({
        type: GROUPS_ACTIONS.LOADING,
        payload: false,
      });
    },
    patchGroupInfo: async (groupInfo, code, status, onSuccessCallBack) => {
      dispatch({
        type: GROUPS_ACTIONS.LOADING,
        payload: true,
      });
      const response = await services.patchGroup(groupInfo, code);
      if (response?.error) {
        dispatch({
          type: GROUPS_ACTIONS.ERROR,
          payload: response?.error ?? GROUPS_CONSTANTS.toast.MESSAGE_FAILURE,
        });
      } else {
        if (response.status === 204) {
          onSuccessCallBack(response.data);
        }
        dispatch({
          type: GROUPS_ACTIONS.SELECTEDVIEW,
          payload: { status: status, group: null },
        });
      }
      dispatch({
        type: GROUPS_ACTIONS.LOADING,
        payload: false,
      });
    },
    refreshError: () => {
      dispatch({
        type: GROUPS_ACTIONS.ERROR,
        payload: null,
      });
    },
    searchGroups: (searchString) => {
      dispatch({
        type: GROUPS_ACTIONS.SEARCHGROUP,
        payload: searchString,
      });
    },
  };
  return actions;
};

function readFacilitiesPage(response) {
  if (response?.error || (response?.status !== 200 && response?.status !== 201)) {
    return { items: [], pagination: null };
  }
  const body = response?.data;
  const items = Array.isArray(body?.data) ? body.data : [];
  return { items, pagination: body?.pagination ?? null };
}

export const useHachwimsFacilityOptions = (selectedHachwims, facilitiesFetchKey) => {
  const groupsServices = useMemo(() => GroupsServices(), []);

  const [filterText, setFilterText] = useState("");
  const [items, setItems] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);

  const requestIdRef = useRef(0);
  const nextPageRef = useRef(0);
  const debounceRef = useRef(null);

  const beginNewSearch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    requestIdRef.current += 1;
    nextPageRef.current = 0;
    setHasNextPage(true);
    setItems([]);
  }, []);

  const fetchPage = useCallback(
    async (page, append, text) => {
      const id = requestIdRef.current;
      if (append) setLoadingMore(true);
      else setLoadingInitial(true);
      try {
        const res = await groupsServices.getHachwimsFacilityOptions({
          text,
          page,
          size: GROUPS_FACILITIES_PICKER.PAGE_SIZE,
        });
        if (id !== requestIdRef.current) return;

        const { items: pageItems, pagination } = readFacilitiesPage(res);
        setItems((prev) => (append ? [...prev, ...pageItems] : [...pageItems]));

        const pageIndex = pagination?.pageIndex ?? page;
        setHasNextPage(
          Boolean(pagination?.hasNext ?? pageItems.length >= GROUPS_FACILITIES_PICKER.PAGE_SIZE)
        );
        nextPageRef.current = pageIndex + 1;
      } catch {
        if (id === requestIdRef.current) {
          if (!append) setItems([]);
          setHasNextPage(false);
        }
      } finally {
        setLoadingInitial(false);
        setLoadingMore(false);
      }
    },
    [groupsServices]
  );

  useEffect(() => {
    beginNewSearch();
    setItems([]);
  }, [facilitiesFetchKey, beginNewSearch]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const onFacilitiesDropdownOpen = useCallback(() => {
    beginNewSearch();
    fetchPage(0, false, filterText);
  }, [beginNewSearch, fetchPage, filterText]);

  const onFacilitiesDropdownClose = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const loadNextPage = useCallback(() => {
    if (!hasNextPage || loadingInitial || loadingMore) return;
    fetchPage(nextPageRef.current, true, filterText);
  }, [fetchPage, filterText, hasNextPage, loadingInitial, loadingMore]);

  const onFacilityListboxScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      if (el.scrollTop + el.clientHeight < el.scrollHeight - GROUPS_FACILITIES_PICKER.LISTBOX_SCROLL_THRESHOLD_PX) return;
      loadNextPage();
    },
    [loadNextPage]
  );

  const onFacilityInputChange = useCallback(
    (_, value, reason) => {
      if (reason !== "input" && reason !== "clear") return;
      setFilterText(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        beginNewSearch();
        fetchPage(0, false, value);
      }, GROUPS_FACILITIES_PICKER.SEARCH_DEBOUNCE_MS);
    },
    [beginNewSearch, fetchPage]
  );

  const hachwimsDropdownOptions = useMemo(() => {
    const out = [...items];
    const selected = Array.isArray(selectedHachwims) ? selectedHachwims : [];
    for (const s of selected) {
      if (s?.id && !out.some((o) => o.id === s.id)) out.push(s);
    }
    return out;
  }, [items, selectedHachwims]);

  return {
    hachwimsDropdownOptions,
    loadingHachwimsOptions: loadingInitial || loadingMore,
    onFacilityInputChange,
    onFacilityListboxScroll,
    onFacilitiesDropdownOpen,
    onFacilitiesDropdownClose,
  };
};
